import React, { useRef, useCallback } from "react"
import styled from "styled-components"

import { Box } from "components/box"
import Icon from "components/icon"
import { useOutsideClick } from "hooks/useOutsideClick"
import { Color } from "theme"

const ModalContainer = styled(Box)`
  position: fixed;
  top: 0;
  max-height: 100%;
  height: 100%;
  width: 100%;
  overflow: auto;

  background-color: ${props => props.theme.colors.white};

  z-index: ${props => props.theme.layers.modal};

  @media screen and (min-width: ${props => props.theme.breakpoints.sm}px) {
    width: initial;
    height: initial;
    top: 50%;
    left: 50%;
    right: auto;
    margin-right: -50%;
    transform: translate(-50%, -50%);
    min-width: 23.4375rem; /* 375px */
  }

  cursor: initial;
`

const ModalOverlay = styled(Box)`
  @media screen and (min-width: ${props => props.theme.breakpoints.sm}px) {
    z-index: ${props => props.theme.layers.modalShim};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, ${props => props.theme.reducedOpacity});
    :hover {
      cursor: pointer;
    }
  }
`

const CloseIcon = styled(Icon)<{ iconColor?: Color }>`
  z-index: ${props => props.theme.layers.middle};
  position: absolute;
  right: 0;
  top: 0;
  padding: ${props => props.theme.space[4]};

  svg {
    width: ${props => props.theme.space[4]};
    height: ${props => props.theme.space[4]};
    path {
      stroke: ${props => props.iconColor ?? props.theme.colors.white};
    }
  }

  :hover {
    opacity: ${props => props.theme.reducedOpacity};
    cursor: pointer;
  }
`

export type ModalProps = {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  iconColor?: Color
}

export const Modal: React.FC<ModalProps> = ({ isOpen, setIsOpen, iconColor, children }) => {
  const nodeRef = useRef<HTMLDivElement>(null)
  const setClosed = useCallback(() => setIsOpen(false), [])

  useOutsideClick(nodeRef, setClosed)

  const getKeyboardFocusableElements = (current: HTMLElement): Element[] => {
    if (!current) {
      return []
    }

    return [
      ...Array.from(
        current.querySelectorAll(
          'a:not([tabindex="-1"]),div[role="button"],button:not([tabindex="-1"]),input:not([tabindex="-1"]),label:not([tabindex="-1"]),textarea:not([tabindex="-1"]),select:not([tabindex="-1"])'
        )
      )
    ].filter(el => !el.hasAttribute("disabled"))
  }

  const handleTabKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const { current } = nodeRef
    const focusableElements = current ? getKeyboardFocusableElements(current) : []

    if (focusableElements.length > 0) {
      const firstElement = focusableElements[0] as HTMLElement
      const lastFocusableElementIndex = focusableElements.length - 1
      const lastElement = focusableElements[lastFocusableElementIndex] as HTMLElement

      let currentIndex = focusableElements.findIndex(el => el === document.activeElement)

      if (!e.shiftKey && document.activeElement !== firstElement) {
        currentIndex = currentIndex === lastFocusableElementIndex ? 0 : currentIndex + 1
        const element = focusableElements[currentIndex] as HTMLElement

        element?.focus()
        return e.preventDefault()
      }

      if (e.shiftKey && document.activeElement !== lastElement) {
        currentIndex = currentIndex === 0 ? lastFocusableElementIndex : currentIndex - 1
        const element = focusableElements[currentIndex] as HTMLElement

        element?.focus()
        return e.preventDefault()
      }
    }

    return null
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation()

    if (e.key === "Escape") {
      setClosed()
    } else if (e.key === "Tab") {
      handleTabKey(e)
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <ModalOverlay role="dialog" aria-hidden={!isOpen} aria-modal={isOpen}>
      <ModalContainer ref={nodeRef} onKeyDown={handleKeyDown}>
        <CloseIcon
          name="cross"
          iconColor={iconColor}
          role="button"
          tabIndex={0}
          onClick={setClosed}
          onKeyPress={(e: KeyboardEvent) => {
            if (e.key === "Enter") {
              setClosed()
            }
          }}
        />
        {children}
      </ModalContainer>
    </ModalOverlay>
  )
}
